import { apiRequest } from './api';
import { ClientConversation, ConversationPreview } from '../store/chat/types';

/**
 * Response from getConversationHistory API
 */
export interface ConversationHistoryResponse {
  conversations: ConversationPreview[];
  dailyMessageCount: number;
  limitHitTime?: string;
  resetTime?: string;
}

/**
 * Fetch conversation history for the current user
 * @param limit Number of conversations to fetch
 * @param offset Pagination offset
 * @returns List of conversations, daily message count, and limit information
 */
export const getConversationHistory = async (
  limit: number = 20,
  offset: number = 0,
): Promise<ConversationHistoryResponse> => {
  return apiRequest<ConversationHistoryResponse>(`/chat/history?limit=${limit}&offset=${offset}`, {
    method: 'GET',
  });
};

/**
 * Fetch a specific conversation by ID
 * @param conversationId The conversation ID to fetch
 * @returns The conversation with messages
 */
export const getConversationById = async (
  conversationId: string,
): Promise<{ conversation: ClientConversation }> => {
  return apiRequest<{ conversation: ClientConversation }>(`/chat/conversation/${conversationId}`, {
    method: 'GET',
  });
};

/**
 * Update a conversation's title
 * @param conversationId The conversation ID to update
 * @param title The new title for the conversation
 * @returns The updated conversation
 */
export const updateConversationTitle = async (
  conversationId: string,
  title: string,
): Promise<{ conversation: ConversationPreview }> => {
  return apiRequest<{ conversation: ConversationPreview }>(`/chat/conversation/${conversationId}/title`, {
    method: 'PUT',
    data: { title },
  });
};

/**
 * Delete a conversation
 * @param conversationId The conversation ID to delete
 * @returns Success indicator
 */
export const deleteConversation = async (
  conversationId: string,
): Promise<{ success: boolean }> => {
  return apiRequest<{ success: boolean }>(`/chat/conversation/${conversationId}`, {
    method: 'DELETE',
  });
};
